import { useState } from 'react'
import { UserRound } from 'lucide-react'
import { GESTOR_SETOR_LABELS } from '@/config/gestorAccess'
import { GestorEquipeDadosPanel } from '@/components/Gestores/GestorEquipeDadosPanel'
import { GestorEquipeFeriasPanel } from '@/components/Gestores/GestorEquipeFeriasPanel'
import { GestorEquipeFeedbackPanel } from '@/components/Gestores/GestorEquipeFeedbackPanel'
import type { GestorEquipeDetail, GestorFeedbackDocumentPayload } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type TabId = 'dados' | 'pagamentos' | 'ferias' | 'feedback'

type Props = {
  detail: GestorEquipeDetail
  initialTab?: TabId
  onSubmitFerias: (form: {
    dataInicio: string
    dataFim: string
    dias: string
    liderancaAprovada: boolean
    observacoes: string
  }) => Promise<void>
  onCreateFeedback: (tipo: GestorFeedbackDocumentPayload['tipo'], payload: GestorFeedbackDocumentPayload) => Promise<void>
  onUpdateFeedback: (feedbackId: string, payload: GestorFeedbackDocumentPayload) => Promise<void>
  onLiberarFeedback: (feedbackId: string) => Promise<void>
  onDeleteFeedback: (feedbackId: string) => Promise<void>
}

function formatMoney(v: number | null) {
  if (v == null) return '—'
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function GestorEquipeDetailContent({
  detail,
  initialTab = 'dados',
  onSubmitFerias,
  onCreateFeedback,
  onUpdateFeedback,
  onLiberarFeedback,
  onDeleteFeedback,
}: Props) {
  const [tab, setTab] = useState<TabId>(initialTab)

  return (
    <div className='space-y-6'>
      <div className='glass-card rounded-xl border border-white/[0.06] px-5 py-5 sm:px-6'>
        <div className='flex items-center gap-3'>
          <div className='flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-500/10'>
            <UserRound size={20} className='text-emerald-600' />
          </div>
          <div>
            <h2 className='text-xl font-bold text-text-primary'>{detail.nomeCompleto}</h2>
            <p className='text-sm text-text-muted'>
              {detail.setor
                ? GESTOR_SETOR_LABELS[detail.setor] ?? detail.setor
                : detail.areaAtuacao ?? 'Setor não definido'}
            </p>
          </div>
        </div>
      </div>

      <div className='flex gap-1 rounded-lg border border-white/[0.06] bg-white/[0.04] p-1'>
        {(['dados', 'pagamentos', 'ferias', 'feedback'] as TabId[]).map((t) => (
          <button
            key={t}
            type='button'
            onClick={() => setTab(t)}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium transition-colors',
              tab === t
                ? 'bg-accent text-white shadow-sm'
                : 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary',
            )}
          >
            {t === 'dados' ? 'Dados' : t === 'feedback' ? 'Feedback' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div
        className={cn(
          'glass-card rounded-xl border border-white/[0.06]',
          tab === 'ferias' || tab === 'dados' ? 'p-4 sm:p-5' : 'p-5 text-sm',
        )}
      >
        {tab === 'dados' ? (
          <GestorEquipeDadosPanel detail={detail} />
        ) : tab === 'pagamentos' ? (
          <ul className='space-y-2'>
            {detail.pagamentos.length === 0 ? (
              <li className='text-text-muted'>Nenhum pagamento registrado.</li>
            ) : (
              detail.pagamentos.map((p) => (
                <li key={p.id} className='rounded-lg border border-border px-3 py-2'>
                  <p className='font-medium'>
                    {p.competencia} — {formatMoney(p.valor)}
                  </p>
                  <p className='text-xs text-text-muted'>
                    {p.pagoEm
                      ? `Pago em ${new Date(p.pagoEm).toLocaleDateString('pt-BR')}`
                      : 'Pendente'}
                  </p>
                </li>
              ))
            )}
          </ul>
        ) : tab === 'ferias' ? (
          <GestorEquipeFeriasPanel detail={detail} onSubmitFerias={onSubmitFerias} />
        ) : (
          <GestorEquipeFeedbackPanel
            pjId={detail.id}
            profissionalNome={detail.nomeCompleto}
            feedbacks={detail.feedbacks ?? []}
            onCreate={onCreateFeedback}
            onUpdate={onUpdateFeedback}
            onLiberar={onLiberarFeedback}
            onDelete={onDeleteFeedback}
          />
        )}
      </div>
    </div>
  )
}
